import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Project } from 'app/modules/projects/projects.types';
import { Account } from 'app/core/models/account.types';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ProjectTemplateSaveComponent } from 'app/modules/projects/modals/project-template-save/project-template-save.component';
import { SharedResourcesService } from 'app/modules/shared-resources/shared-resources.service';
import { SharedResource } from 'app/modules/shared-resources/shared-resources.types';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { FoldersTabService } from 'app/shared/modules/folders/folders.service';
import { AccountService } from 'app/core';
import { RelatedToType } from 'app/shared/enums';
import { ProjectsService } from 'app/modules/projects/projects.service';

@Component({
  selector: 'shared-resource-project-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharedResourceProjectDashboardComponent extends OnDestroyMixin implements OnInit {
  project: Project;
  sharedResource: SharedResource;
  data: any;
  panels: any[];
  account: Account;

  private _dashboardDefinition: any;

  constructor(
    private readonly matDialog: MatDialog,
    private readonly sharedResourcesService: SharedResourcesService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly foldersTabService: FoldersTabService,
    private readonly accountService: AccountService,
    private readonly projectService: ProjectsService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.sharedResourcesService.sharedResource$
      .pipe(untilComponentDestroyed(this))
      .subscribe((sharedResource: SharedResource) => {
        if (sharedResource) {
          this.sharedResource = sharedResource;
          this.project = this.sharedResource.project;
          this._dashboardDefinition = this.project.template?.definition.dashboard;
          this.panels = this._dashboardDefinition.panels;
          this.account = this.sharedResource.account;
          this.accountService.setAccount(this.account);
          this.projectService.accountId = this.account.id;
          this.foldersTabService.accountId = this.account.id;

          let allowedPanels = [];
          for (let panelCol of this.panels) {
            for (let panelDef of panelCol) {
              allowedPanels.push(panelDef.id);
            }
          }

          if (this.sharedResource.permissions) {
            if (this.sharedResource.permissions.panels) {
              allowedPanels = this.sharedResource.permissions.panels;
            }
          } else {
            this._dashboardDefinition = this.project.template?.definition.dashboard;
            this.panels = this._dashboardDefinition.panels;
          }

          let newPanels = [];
          let firstCol = [];
          let secondCol = [];
          let thirdCol = [];

          let index = 0;
          for (let panelCol of this.panels) {
            for (let panelDef of panelCol) {
              if (allowedPanels.includes(panelDef.id)) {
                if (panelDef.type === 'form') {
                  panelDef.meta.fields = this.sanitizeFormPanelFields(panelDef.meta.fields);

                  if (index % 2 == 0) {
                    firstCol.push(panelDef);
                  } else {
                    secondCol.push(panelDef);
                  }
                } else {
                  thirdCol.push(panelDef);
                }
                index++;
              }
            }
          }
          newPanels[0] = firstCol;
          newPanels[1] = secondCol;
          newPanels[2] = thirdCol;

          this.panels = newPanels;

          const options = [
            'investment_types',
            'property_types',
            'project_types',
            'project_stages',
            'market_statuses',
            'project_phases',
            'teams',
            'vehicles',
            'users',
          ].reduce((p, c) => ({ ...p, [c]: this.sharedResource.account[c] }), {});

          this.data = {
            fieldDefs: this.sharedResource.account['fields'],
            options,
          };
        }

        this.changeDetectorRef.markForCheck();
      });
  }

  onEditPanelDef($event): void {
    // Let's find the panel with this id and replace it
    for (const col in this.panels) {
      for (const row in this.panels[col]) {
        let panel = this.panels[col][row];
        if (panel.id == $event.id) {
          this.panels[col][row] = $event;
        }
      }
    }
    this._dashboardDefinition.panels = this.panels;
    const projectTemplate = this.project.template;
    projectTemplate.definition.dashboard = this._dashboardDefinition;
    this.showTemplateSaveDialog(projectTemplate);
  }

  showTemplateSaveDialog(projectTemplate): void {
    this.matDialog
      .open(ProjectTemplateSaveComponent, {
        panelClass: 'mailbox-compose-dialog',
        data: {
          project_template: projectTemplate,
          project_id: this.project.id,
          related_to_type: RelatedToType.Project,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          //this._projectsService.bustProjectCache();
          //refresh project so that if we show this modal again it will display the correct template name
          //this._projectsService.getProjectById(this.project.id, 'json', true).subscribe();
        }
      });
  }

  private sanitizeFormPanelFields(fields: string[][]): string[][] {
    const exceptions = ['template_id'];

    return fields.map((innerFieldArray) =>
      (innerFieldArray ?? []).filter((fieldName) => !exceptions.includes(fieldName)),
    );
  }
}
